import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, ListSubheading, Spinner, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { reorderRisks } from 'store/entities/risks/risks';
import translations from 'src/internationalisation/translation-map.json';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { selectRisksByType } from 'src/store/entities/risks/selectors';
import { IRisk } from 'src/common/interfaces/risk.interface';

import type { TRootState } from 'src/store/store-types';

type TRisksListByTypeProps = {
  type: string;
  onClickItem: (id: string) => void;
  onClickDelete: (id: string) => void;
  risksList: {
    id: string;
    name: string;
    draggable: boolean;
    active: boolean;
    actions: {
      label: string;
      icon: string;
      onClick?: () => void;
      disabled?: boolean;
    }[];
  }[];
  reorderRisks: any;
  isAdding: boolean;
  onClickAdd: () => void;
  projectId: string;
  isPageDisabled: boolean;
};

const RisksListByType = ({
  type,
  onClickItem,
  onClickDelete,
  risksList,
  reorderRisks,
  isAdding,
  onClickAdd,
  projectId,
  isPageDisabled,
}: TRisksListByTypeProps) => {
  const { t } = useTranslation();
  const isModifierForCostActive = FeatureFlags.isActive('Project Modifier For Cost');

  return (
    <>
      {isModifierForCostActive && (
        <ListSubheading
          item={{
            name: t(translations[type as keyof typeof translations]),
            actions: [
              {
                label: t(translations.add),
                icon: isAdding ? <Spinner tiny dark /> : 'add',
                testId: `${type}-risk-list-subheading-add-button`,
                primary: true,
                onClick: onClickAdd,
                disabled: isAdding || isPageDisabled,
              },
            ],
          }}
        />
      )}
      <List
        noHeader
        testId={`${type}-risk-list`}
        list={{
          items: risksList.map((risk) => ({
            testId: `${type}-risk-list-${convertToKebabCase(risk.name)}-item`,
            ...risk,
            onClick: () => onClickItem(risk.id),
            actions: risk.actions.map((action) => ({
              testId: `${type}-risk-list-${convertToKebabCase(risk.name)}-delete-button`,
              ...action,
              onClick: () => onClickDelete(risk.id),
              disabled: isPageDisabled,
            })),
          })),
        }}
        draggable={!isPageDisabled}
        onListReorder={(params) => reorderRisks(params, risksList, projectId)}
      />
    </>
  );
};

const mapStateToProps = (state: TRootState, { type }: { type: string }) => {
  const risksListByType = selectRisksByType(state, type);

  const risksList = risksListByType.map((item: IRisk & { active: boolean }) => ({
    id: item.riskId,
    name: item.name,
    draggable: true,
    active: item.active,
    actions: [
      {
        label: 'Delete',
        icon: 'delete',
      },
    ],
  }));
  return {
    risksList,
  };
};

const mapDispatchToProps = {
  reorderRisks,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(RisksListByType));

export { Container as RisksListByType };
